/**
 * Volume Discount Drawer behavior
 * ---------------------------------------------------------------------------
 * Ported out of the inline <script> in snippets/volume-discount-drawer.liquid
 * so it ALSO runs for drawers injected via innerHTML (quick-buy drawer/popover
 * on collection pages and product grids). Inline scripts set through innerHTML
 * never execute, which is why the buy-more-save-more modal had no behavior in
 * the quick-buy context.
 *
 * Responsibilities:
 *   - Tier selection (click + roving keyboard nav within the radiogroup)
 *   - Add selected tier quantity to cart, then refresh the mini-cart
 *   - Live-recalculate tier prices / savings / free-ship flag on variant:changed
 *
 * Opening/closing the drawer is handled by the <drawer-content> custom element
 * + the toggle-button hint (theme.js). This file only wires up the contents.
 *
 * All per-product config is read from data-* attributes on [data-volume-tiers],
 * so no Liquid is baked into this file and a single instance can serve any
 * product (including ones loaded later via AJAX).
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function trackEvent(eventName, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, data));
  }

  function initVolumeDrawer(drawer) {
    if (!drawer || drawer.dataset.volumeInit === '1') return;

    var tiersContainer = drawer.querySelector('[data-volume-tiers]');
    if (!tiersContainer) return;

    var tiers = Array.prototype.slice.call(drawer.querySelectorAll('.volume-tier'));
    var addBtn = drawer.querySelector('[data-volume-add-to-cart]');
    var qtyDisplay = drawer.querySelector('[data-volume-qty]');
    var totalDisplay = drawer.querySelector('[data-volume-total]');

    // Mark initialized up front so a re-entrant observer/event can't double-bind.
    drawer.dataset.volumeInit = '1';

    // Discount map from the data attribute (single source = settings.bundle_tiers).
    var discountMap = {};
    (tiersContainer.dataset.discountMap || '1:0').split(',').forEach(function (p) {
      var parts = p.split(':');
      discountMap[parseInt(parts[0], 10)] = parseInt(parts[1], 10) || 0;
    });

    var freeShipCents = parseInt(tiersContainer.dataset.freeShipCents, 10) || 0;
    var productId = tiersContainer.dataset.productId;
    var productTitle = tiersContainer.dataset.productTitle || '';

    var selectedQty = parseInt(tiersContainer.dataset.defaultQty, 10) || 1;
    var currentVariantId = tiersContainer.dataset.variantId;

    // Read base price live from the dataset so variant:changed is reflected.
    function getBasePrice() {
      return parseInt(tiersContainer.dataset.basePrice, 10);
    }

    function calculateTotal(qty) {
      var discount = discountMap[qty] || 0;
      var subtotal = getBasePrice() * qty;
      return Math.round(subtotal * (1 - discount / 100));
    }

    function updateTierAriaLabel(tier, qty, finalPrice) {
      var discount = discountMap[qty] || 0;
      if (qty === 1) {
        tier.setAttribute('aria-label', '1 Pack - ' + formatMoney(finalPrice));
      } else {
        tier.setAttribute('aria-label', qty + ' Packs, ' + discount + '% off - ' + formatMoney(finalPrice) + ' total');
      }
    }

    function selectTier(qty, isUserAction) {
      selectedQty = qty;
      tiers.forEach(function (tier) {
        var tierQty = parseInt(tier.dataset.quantity, 10);
        var isSelected = tierQty === qty;
        tier.classList.toggle('volume-tier--selected', isSelected);
        tier.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        // Keep the radiogroup keyboard-reachable: only the selected tier is in the tab order.
        tier.setAttribute('tabindex', isSelected ? '0' : '-1');
      });
      var total = calculateTotal(qty);
      if (qtyDisplay) qtyDisplay.textContent = qty;
      if (totalDisplay) totalDisplay.textContent = formatMoney(total);
      if (isUserAction) {
        trackEvent('volume_tier_select', {
          event_label: productTitle,
          product_id: productId,
          quantity: qty,
          discount_percent: discountMap[qty] || 0,
          total_value: total / 100
        });
      }
    }

    // ---------------------------------------------------------------------
    // Sync the preselected tier to the PDP quantity stepper on open.
    //
    // data-default-qty is the "Popular" tier, baked in at render time. Without
    // this, a shopper who has already stepped the PDP to 6 opens the drawer and
    // sees 4 selected with the CTA reading "Add 4 Packs to Cart" - one click
    // away from adding less than they had chosen.
    //
    // Exact tier match wins; otherwise fall to the highest tier at or below the
    // chosen quantity (7 -> 6), so we never silently upsell either. If the
    // stepper is below every tier, the rendered default stands.
    // ---------------------------------------------------------------------
    function pdpQuantity() {
      var inputs = document.querySelectorAll('form[is="product-form"] input[name="quantity"], .product__info input[name="quantity"]');
      for (var i = 0; i < inputs.length; i++) {
        // Skip the sticky bar / quick-buy copies that are not on screen.
        if (inputs[i].offsetParent !== null) {
          var v = parseInt(inputs[i].value, 10);
          if (v > 0) return v;
        }
      }
      return 0;
    }

    function syncToPdpQuantity() {
      var qty = pdpQuantity();
      if (!qty) return;

      var available = tiers
        .map(function (tier) { return parseInt(tier.dataset.quantity, 10); })
        .filter(function (n) { return n > 0; })
        .sort(function (a, b) { return a - b; });
      if (!available.length) return;

      var match = 0;
      for (var i = 0; i < available.length; i++) {
        if (available[i] <= qty) match = available[i];
      }
      if (match && match !== selectedQty) selectTier(match, false);

      // Scroll the selected tier into view inside the drawer's own scroller.
      // On a short viewport the tier list overflows, so a 6-pack selection sits
      // below the fold: the CTA reads the right total but the shopper sees the
      // 4-pack card highlighted-adjacent and no confirmation of what is chosen.
      // Only .drawer__content is scrolled - never the document - so opening the
      // drawer cannot move the page behind it.
      var selected = null;
      for (var t = 0; t < tiers.length; t++) {
        if (parseInt(tiers[t].dataset.quantity, 10) === selectedQty) { selected = tiers[t]; break; }
      }
      if (!selected) return;

      var scroller = drawer.querySelector('.drawer__content');
      if (!scroller) return;

      window.requestAnimationFrame(function () {
        var sRect = scroller.getBoundingClientRect();
        var tRect = selected.getBoundingClientRect();
        var pad = 16;
        if (tRect.bottom > sRect.bottom) {
          scroller.scrollTop += (tRect.bottom - sRect.bottom) + pad;
        } else if (tRect.top < sRect.top) {
          scroller.scrollTop -= (sRect.top - tRect.top) + pad;
        }
      });
    }

    // The drawer is opened by a toggle-button in theme.js, which sets [open].
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'open' && drawer.hasAttribute('open')) {
            syncToPdpQuantity();
          }
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
    if (drawer.hasAttribute('open')) syncToPdpQuantity();

    tiers.forEach(function (tier) {
      tier.addEventListener('click', function () {
        selectTier(parseInt(tier.dataset.quantity, 10), true);
      });
      tier.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          var next = tier.nextElementSibling;
          if (next && next.classList.contains('volume-tier')) {
            next.focus();
            selectTier(parseInt(next.dataset.quantity, 10), true);
          }
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var prev = tier.previousElementSibling;
          if (prev && prev.classList.contains('volume-tier')) {
            prev.focus();
            selectTier(parseInt(prev.dataset.quantity, 10), true);
          }
        }
      });
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var total = calculateTotal(selectedQty);
        var discountPercent = discountMap[selectedQty] || 0;

        addBtn.setAttribute('aria-busy', 'true');
        addBtn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: currentVariantId, quantity: selectedQty, sections: ['mini-cart'] })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Failed to add to cart');
            return response.json();
          })
          .then(function (cartResponse) {
            trackEvent('volume_add_to_cart', {
              event_label: productTitle,
              product_id: productId,
              variant_id: currentVariantId,
              quantity: selectedQty,
              discount_percent: discountPercent,
              total_value: total / 100,
              currency: 'USD'
            });

            return fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cartContent) {
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartContent } })
                );
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:refresh', {
                    bubbles: true,
                    detail: { cart: cartResponse, openMiniCart: true }
                  })
                );
                var closeBtn = drawer.querySelector('[data-action="close"]');
                if (closeBtn) closeBtn.click();
              });
          })
          .catch(function (error) {
            console.error('Volume add to cart error:', error);
            window.alert('There was a problem adding to cart. Please try again.');
          })
          .then(function () {
            addBtn.removeAttribute('aria-busy');
            addBtn.disabled = false;
            var loaderTextEl = addBtn.querySelector('.loader-button__text');
            var labelHTML =
              'Add&nbsp;<span data-volume-qty>' + selectedQty +
              '</span>&nbsp;Packs to Cart -&nbsp;<span data-volume-total>' +
              formatMoney(calculateTotal(selectedQty)) + '</span>';
            if (loaderTextEl) {
              loaderTextEl.innerHTML = labelHTML;
            } else {
              addBtn.innerHTML = labelHTML;
            }
            // Re-cache the inner spans after the label re-render.
            qtyDisplay = drawer.querySelector('[data-volume-qty]');
            totalDisplay = drawer.querySelector('[data-volume-total]');
          });
      });
    }

    // Recalculate every tier (price, savings, free-shipping flag) on variant change.
    // variant:changed bubbles from the form to document, so a document-level
    // listener catches both PDP and quick-buy variant switches. We scope updates
    // to this drawer's own product so multiple drawers on a page stay independent.
    document.addEventListener('variant:changed', function (e) {
      if (!e.detail || !e.detail.variant) return;

      var newVariant = e.detail.variant;
      // Only react to changes for this drawer's product when we can tell them apart.
      if (newVariant.product_id && productId && String(newVariant.product_id) !== String(productId)) {
        return;
      }

      currentVariantId = newVariant.id;
      tiersContainer.dataset.variantId = newVariant.id;
      var newBasePrice = newVariant.price;
      tiersContainer.dataset.basePrice = newBasePrice;

      tiers.forEach(function (tier) {
        var qty = parseInt(tier.dataset.quantity, 10);
        var discount = discountMap[qty] || 0;
        var subtotal = newBasePrice * qty;
        var final = Math.round(subtotal * (1 - discount / 100));
        var perUnit = Math.round(final / qty);

        var totalEl = tier.querySelector('[data-tier-total]');
        if (totalEl) totalEl.textContent = formatMoney(final);

        var saveEl = tier.querySelector('[data-tier-save]');
        if (saveEl && discount > 0) saveEl.textContent = 'Save ' + formatMoney(subtotal - final);

        var perEl = tier.querySelector('[data-tier-per]');
        if (perEl && discount > 0) perEl.textContent = formatMoney(perUnit) + '/ea';

        var shipEl = tier.querySelector('.volume-tier__ship');
        if (shipEl) {
          if (freeShipCents > 0 && final >= freeShipCents) {
            shipEl.removeAttribute('hidden');
          } else {
            shipEl.setAttribute('hidden', '');
          }
        }

        updateTierAriaLabel(tier, qty, final);
      });

      var newTotal = calculateTotal(selectedQty);
      var loaderEl = addBtn ? addBtn.querySelector('.loader-button__text') : null;
      if (loaderEl) {
        var qtyEl = loaderEl.querySelector('[data-volume-qty]') || qtyDisplay;
        var totalEl2 = loaderEl.querySelector('[data-volume-total]') || totalDisplay;
        if (qtyEl) qtyEl.textContent = selectedQty;
        if (totalEl2) totalEl2.textContent = formatMoney(newTotal);
      } else {
        if (qtyDisplay) qtyDisplay.textContent = selectedQty;
        if (totalDisplay) totalDisplay.textContent = formatMoney(newTotal);
      }
    });
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var drawers = scope.querySelectorAll('[id^="volume-drawer-"]');
    Array.prototype.forEach.call(drawers, initVolumeDrawer);
    // When `root` is itself a drawer (e.g. the injected quick-buy node).
    // Read the id via getAttribute (always a string|null): on a <form> that has a
    // control named "id" - the variant input on every product form - `root.id`
    // returns that element instead of a string, and .indexOf on it throws.
    if (root && root.nodeType === 1 && (root.getAttribute('id') || '').indexOf('volume-drawer-') === 0) {
      initVolumeDrawer(root);
    }
  }

  // Initial page load (PDP and any drawers already in the DOM).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Catch drawers injected later via innerHTML (quick-buy drawer/popover).
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          // getAttribute('id') is always string|null; node.id can be an element
          // when the added node is a <form> containing a control named "id",
          // which is what was throwing "node.id.indexOf is not a function".
          if ((node.getAttribute('id') || '').indexOf('volume-drawer-') === 0) {
            initVolumeDrawer(node);
          } else if (node.querySelector) {
            initAll(node);
          }
        }
      }
    });
    observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
  }

  // Expose for manual/late initialization if ever needed.
  window.initVolumeDrawer = initVolumeDrawer;
})();